import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Footer from './components/Footer/Footer';
import Header from './components/Header/Header';
import ScrollToTop from './components/ScrollToTop';
import SocialMetaDefaults from './seo/SocialMetaDefaults';
import ErrorBoundary from './components/ErrorBoundary';

const Homepage = lazy(() => import('./components/pages/Homepage/HomepageMain'));
const Discovery = lazy(() => import('./components/pages/Discovery/DiscoveryMain'));
const Academics = lazy(() => import('./components/pages/Academics/AcademicsMain'));
const LessonsLibrary = lazy(() => import('./components/pages/Lessons/LessonsLibraryMain'));
const Admission = lazy(() => import('./components/pages/Admission/AdmissionMain'));
const Support = lazy(() => import('./components/pages/Support/SupportMain'));
const Transcript = lazy(() => import('./components/pages/Transcript/TranscriptMain'));
const LoginPortal = lazy(() => import('./components/pages/Auth/LoginPortal'));
const ResetPasswordPage = lazy(() => import('./components/pages/Auth/ResetPasswordPage'));
const AdminLogin = lazy(() => import('./components/pages/Admin/AdminLogin'));
const AdminHomePage = lazy(() => import('./components/pages/Admin/AdminHomePage'));
const AdminDashboard = lazy(() => import('./components/pages/Admin/AdminDashboard'));
const AdminTranscriptPage = lazy(() => import('./components/pages/Admin/AdminTranscriptPage'));
const AdminProgressPage = lazy(() => import('./components/pages/Admin/AdminProgressPage'));
const AdminAuditTrailPage = lazy(() => import('./components/pages/Admin/AdminAuditTrailPage'));
const AdminDocumentsPage = lazy(() => import('./components/pages/Admin/AdminDocumentsPage'));
const AdminTransferSopPage = lazy(() => import('./components/pages/Admin/AdminTransferSopPage'));
const AdminCoursesPage = lazy(() => import('./components/pages/Admin/AdminCoursesPage'));
const AdminEmailLogsPage = lazy(() => import('./components/pages/Admin/AdminEmailLogsPage'));
const AdminCalendarPage = lazy(() => import('./components/pages/Admin/AdminCalendarPage'));
const AdminStaffPage = lazy(() => import('./components/pages/Admin/AdminStaffPage'));
const AdminSetPasswordPage = lazy(() => import('./components/pages/Admin/AdminSetPasswordPage'));
const PrincipalReviewPage = lazy(() => import('./components/pages/Admin/PrincipalReviewPage'));
const CalendarPage = lazy(() => import('./components/pages/Calendar/CalendarPage'));
const DiplomaPage = lazy(() => import('./components/pages/Diploma/DiplomaPage'));
const SchoolProfilePage = lazy(() => import('./components/pages/SchoolProfile/SchoolProfilePage'));
const AboutPage = lazy(() => import('./components/pages/About/AboutPage'));
const HandbookPage = lazy(() => import('./components/pages/Handbook/HandbookPage'));
const NotFound = lazy(() => import('./components/pages/NotFound'));
const PrivacyPolicy = lazy(() => import('./components/pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./components/pages/TermsOfUse'));

// Pathways
const PathwaysHub = lazy(() => import('./components/pages/Pathways/PathwaysHub'));
const PsychologyPathway = lazy(() => import('./components/pages/Pathways/PsychologyPathway'));
const CSPathway = lazy(() => import('./components/pages/Pathways/CSPathway'));
const BusinessPathway = lazy(() => import('./components/pages/Pathways/BusinessPathway'));
const EconomicsPathway = lazy(() => import('./components/pages/Pathways/EconomicsPathway'));
const EngineeringPathway = lazy(() => import('./components/pages/Pathways/EngineeringPathway'));
const MathDataPathway = lazy(() => import('./components/pages/Pathways/MathDataPathway'));
const CommunicationsPathway = lazy(() => import('./components/pages/Pathways/CommunicationsPathway'));
const ArtsDesignPathway = lazy(() => import('./components/pages/Pathways/ArtsDesignPathway'));

// Learning portal
const LearnDashboard = lazy(() => import('./components/pages/Learn/LearnDashboard'));
const CoursePage = lazy(() => import('./components/pages/Learn/CoursePage'));
const ModulePage = lazy(() => import('./components/pages/Learn/ModulePage'));
const ExamPage = lazy(() => import('./components/pages/Learn/ExamPage'));
const GradesPage = lazy(() => import('./components/pages/Learn/GradesPage'));
const SyllabusPage = lazy(() => import('./components/pages/Learn/SyllabusPage'));
const ProfilePage = lazy(() => import('./components/pages/Profile/ProfilePage'));
const PricingPage = lazy(() => import('./components/pages/Pricing/PricingPage'));
const ParentDashboardDemo = lazy(() => import('./components/pages/Parent/ParentDashboardDemo'));
const ParentLogin = lazy(() => import('./components/pages/Parent/ParentLogin'));
const ParentDashboard = lazy(() => import('./components/pages/Parent/ParentDashboard'));
const ParentTranscriptPage = lazy(() => import('./components/pages/Parent/ParentTranscriptPage'));
const AssignmentQueue = lazy(() => import('./components/pages/Admin/AssignmentQueue'));
const ApplicationsQueue = lazy(() => import('./components/pages/Admin/ApplicationsQueue'));
const AdminSubscriptionsPage = lazy(() => import('./components/pages/Admin/AdminSubscriptionsPage'));
const AdminWeeklyReportPage = lazy(() => import('./components/pages/Admin/AdminWeeklyReportPage'));
const ApplyForm = lazy(() => import('./components/pages/Apply/ApplyForm'));
const InterestConfirmationPage = lazy(() => import('./components/pages/Apply/InterestConfirmationPage'));
const ApplicationStatusPage = lazy(() => import('./components/pages/Apply/ApplicationStatusPage'));
const VerifyPage = lazy(() => import('./components/pages/Verify/VerifyPage'));
const EnrollmentVerificationPage = lazy(() => import('./components/pages/EnrollmentVerification/EnrollmentVerificationPage'));
const EnrollmentVerificationPublicPage = lazy(() => import('./components/pages/EnrollmentVerification/EnrollmentVerificationPublicPage'));
const WelcomePage = lazy(() => import('./components/pages/Welcome/WelcomePage'));
const TransferStudentsPage = lazy(() => import('./components/pages/TransferStudents/TransferStudentsPage'));
const TrustCenterPage = lazy(() => import('./components/pages/TrustCenter/TrustCenterPage'));
const AssessmentProofPage = lazy(() => import('./components/pages/AssessmentProof/AssessmentProofPage'));
const ConsultationPage = lazy(() => import('./components/pages/Consultation/ConsultationPage'));
const GraduateStoriesPage = lazy(() => import('./components/pages/Graduates/GraduateStoriesPage'));
const RefundPolicyPage = lazy(() => import('./components/pages/RefundPolicy/RefundPolicyPage'));
const G9ReadinessAssessmentPage = lazy(() => import('./components/pages/Placement/G9ReadinessAssessmentPage'));

const LANGUAGE_STORAGE_KEY = 'giis-language';

function readInitialLanguage() {
  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved === 'en' || saved === 'zh') {
      return saved;
    }
  } catch {
    /* ignore */
  }
  const browserLanguage = navigator.language || navigator.userLanguage;
  return browserLanguage && browserLanguage.includes('zh') ? 'zh' : 'en';
}

function RouteFallback() {
  return (
    <div className="py-5 text-center text-muted" role="status">
      Loading…
    </div>
  );
}

function App() {
  const [language, setLanguage] = useState(readInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language === 'en' ? 'en' : 'zh';
  }, [language]);

  useEffect(() => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
      /* ignore */
    }
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prevLanguage) => (prevLanguage === 'en' ? 'zh' : 'en'));
  };

  const location = useLocation();
  const isTranscript = location.pathname === '/transcript';
  const isAdmin = location.pathname.startsWith('/admin');
  const isLearn = location.pathname.startsWith('/learn');
  const isProfile = location.pathname === '/profile';
  const isEnrollmentVerification = location.pathname.startsWith('/enrollment-verification') || location.pathname.startsWith('/verify/enrollment/');
  const isParent = location.pathname.startsWith('/parent');
  const isApplicationStatus = location.pathname === '/application/status';
  const hideChrome = isTranscript || isAdmin || isLearn || isProfile || isParent || isEnrollmentVerification || isApplicationStatus;

  return (
     <>
      <SocialMetaDefaults />
      <ScrollToTop />
      {!hideChrome && <Header language={language} toggleLanguage={toggleLanguage} />}
      <main className="container-fluid">
       <ErrorBoundary>
       <Suspense fallback={<RouteFallback />}>
       <Routes>
         <Route path="/" element={<Homepage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/discovery" element={<Discovery language={language} toggleLanguage={toggleLanguage}  />} />
         <Route path="/academics" element={<Academics language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/lessons" element={<LessonsLibrary language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admission" element={<Admission language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/support" element={<Support language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/transcript" element={<Transcript language={language}/>}/>
         <Route path="/login" element={<LoginPortal language={language} />} />
         <Route path="/reset-password" element={<ResetPasswordPage language={language} />} />
         <Route path="/register" element={<Navigate to="/login?tab=register" replace />} />
         <Route path="/admin/login" element={<AdminLogin language={language} />} />
         <Route path="/admin/set-password" element={<AdminSetPasswordPage />} />
         <Route path="/admin" element={<AdminHomePage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/roster" element={<AdminDashboard language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/transcript/:studentId" element={<AdminTranscriptPage language={language} />} />
         <Route path="/admin/progress" element={<AdminProgressPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/documents" element={<AdminDocumentsPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/transfer-sop" element={<AdminTransferSopPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/courses" element={<AdminCoursesPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/email-logs" element={<AdminEmailLogsPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/calendar" element={<AdminCalendarPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/staff" element={<AdminStaffPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/principal-review" element={<PrincipalReviewPage language={language} toggleLanguage={toggleLanguage} />} />
        <Route path="/admin/students/:studentId/audit-trail" element={<AdminAuditTrailPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/calendar" element={<CalendarPage language={language} />} />
         {/* Pathways hub */}
         <Route path="/pathways" element={<PathwaysHub language={language} toggleLanguage={toggleLanguage} />} />
         {/* Individual pathways */}
         <Route path="/pathways/psychology" element={<PsychologyPathway language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/pathways/cs" element={<CSPathway language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/pathways/business" element={<BusinessPathway language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/pathways/economics" element={<EconomicsPathway language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/pathways/engineering" element={<EngineeringPathway language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/pathways/math" element={<MathDataPathway language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/pathways/communications" element={<CommunicationsPathway language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/pathways/arts" element={<ArtsDesignPathway language={language} toggleLanguage={toggleLanguage} />} />
         {/* Learning portal */}
         <Route path="/learn" element={<LearnDashboard language={language} />} />
         <Route path="/learn/:slug" element={<CoursePage language={language} />} />
         <Route path="/learn/:slug/module/:order" element={<ModulePage language={language} />} />
         <Route path="/learn/:slug/exam" element={<ExamPage language={language} />} />
         <Route path="/learn/:slug/grades" element={<GradesPage language={language} />} />
         <Route path="/learn/:slug/syllabus" element={<SyllabusPage language={language} />} />
         <Route path="/diploma/:studentId" element={<DiplomaPage language={language} />} />
        <Route path="/school-profile" element={<SchoolProfilePage language={language} />} />
         <Route path="/about" element={<AboutPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/about/principal" element={<Navigate to="/about" replace />} />
         <Route path="/about/leadership" element={<Navigate to="/about" replace />} />
         <Route path="/about/faculty" element={<Navigate to="/about" replace />} />
         <Route path="/handbook" element={<HandbookPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/student-handbook" element={<Navigate to="/handbook" replace />} />
         <Route path="/profile" element={<ProfilePage language={language} />} />
         <Route path="/enrollment-verification" element={<EnrollmentVerificationPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/enrollment-verification/:studentId" element={<EnrollmentVerificationPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/pricing" element={<PricingPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/transfer-students" element={<TransferStudentsPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/trust-center" element={<TrustCenterPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/consultation" element={<ConsultationPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/graduates" element={<GraduateStoriesPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/assessment-proof" element={<AssessmentProofPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/placement/g9-readiness" element={<G9ReadinessAssessmentPage language={language} />} />
         <Route path="/refund-policy" element={<RefundPolicyPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/parent/demo" element={<ParentDashboardDemo language={language} />} />
         <Route path="/parent/login" element={<ParentLogin language={language} />} />
         <Route path="/parent/dashboard" element={<ParentDashboard language={language} />} />
         <Route path="/parent/transcript" element={<ParentTranscriptPage language={language} />} />
         <Route path="/admin/assignments" element={<AssignmentQueue language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/applications" element={<ApplicationsQueue language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/subscriptions" element={<AdminSubscriptionsPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/admin/weekly-report" element={<AdminWeeklyReportPage language={language} toggleLanguage={toggleLanguage} />} />
         <Route path="/apply" element={<ApplyForm language={language} />} />
         <Route path="/application/confirm" element={<InterestConfirmationPage language={language} />} />
         <Route path="/application/status" element={<ApplicationStatusPage language={language} />} />
         <Route path="/welcome" element={<WelcomePage language={language} />} />
         <Route path="/verify/enrollment/:token" element={<EnrollmentVerificationPublicPage />} />
         <Route path="/verify/:code" element={<VerifyPage />} />
         <Route path="/privacy" element={<PrivacyPolicy language={language} />} />
         <Route path="/terms" element={<TermsOfUse language={language} />} />
         <Route path="*" element={<NotFound language={language} />} />
       </Routes>
       </Suspense>
       </ErrorBoundary>
      </main>
      {!hideChrome && <Footer language={language} />}
     </>
  );
}

export default App;
